import React from 'react';
import { Alert, Button, Col, FloatingLabel, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { customerFoldersByUser, importCustomerLinksByUser } from '@src/Api';
import type { DynamicsBcSettingsModuleProps } from '@src/pages/settings/types';
import { CustomerContactPriorities } from './customerContact';
import { DynamicsBcDefaultShipmentMethod } from './default_shipment_method';

export const CustomerForm: React.FC<DynamicsBcSettingsModuleProps> = ({ dynamicsBcUserDefaultData, setDynamicsBcUserDefaultData }) => {
  const bcMaster = dynamicsBcUserDefaultData.customer_data_master === 'dynamicsBc';
  const [folders, setFolders] = React.useState<Array<{ id: number; name: string; path: string }>>([]);
  const [folderError, setFolderError] = React.useState('');
  const [confirmImport, setConfirmImport] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const [importError, setImportError] = React.useState('');
  const [importResult, setImportResult] = React.useState<{
    linked: number;
    unmatchedRentman: number;
    issues: Array<{ customerNumber: string; reason: string }>;
    contactLinks?: {
      linked: number;
      issues: Array<{ customerNumber: string; contactNumber: string; reason: string }>;
    };
  } | null>(null);
  const importLinks = async () => {
    if (importing) return;
    setConfirmImport(false);
    setImporting(true);
    setImportError('');
    setImportResult(null);
    try {
      const response = await importCustomerLinksByUser();
      if (response?.status === 200) setImportResult(response.data);
      else setImportError(response?.data?.error || 'Unable to import customer links.');
    } catch {
      setImportError('Unable to import customer links.');
    } finally {
      setImporting(false);
    }
  };
  React.useEffect(() => {
    if (!bcMaster) return;
    let active = true;
    customerFoldersByUser()
      .then((response) => {
        if (!active) return;
        if (!response || response.status !== 200 || !Array.isArray(response.data)) {
          setFolderError('Unable to load Rentman customer folders. Switch master and try again.');
        } else {
          setFolders(response.data);
          setFolderError('');
        }
      })
      .catch(() => {
        if (active) setFolderError('Unable to load Rentman customer folders.');
      });
    return () => {
      active = false;
    };
  }, [bcMaster]);

  return (
    <>
      <Form.Group as={Row} className="mb-3" controlId="customer-data-master">
        <Form.Label column sm={6}>
          Customer data master
        </Form.Label>
        <Col sm={6}>
          <FloatingLabel controlId="customer-data-master" label="Select customer data master">
            <Form.Select
              value={dynamicsBcUserDefaultData.customer_data_master || 'rentman'}
              onChange={(event) =>
                setDynamicsBcUserDefaultData?.({ ...dynamicsBcUserDefaultData, customer_data_master: event.target.value as 'rentman' | 'dynamicsBc' })
              }
            >
              <option value="rentman">Rentman</option>
              <option value="dynamicsBc">Business Central</option>
            </Form.Select>
          </FloatingLabel>
          {bcMaster && (
            <Form.Text className="d-block mt-2" muted>
              Customer and contact changes are sent from BC to Rentman. Invoices use your existing BC customers.
            </Form.Text>
          )}
        </Col>
      </Form.Group>
      {bcMaster ? (
        <>
          <Form.Group as={Row} className="mb-3" controlId="customer-sync-use-selection-field">
            <Form.Label column sm={6}>
              Only sync customers selected in Business Central
            </Form.Label>
            <Col sm={6}>
              <Form.Check
                type="switch"
                label="Enable"
                checked={dynamicsBcUserDefaultData.customer_sync_use_selection_field !== false}
                onChange={(event) => setDynamicsBcUserDefaultData?.({ ...dynamicsBcUserDefaultData, customer_sync_use_selection_field: event.target.checked })}
              />
              <Form.Text className="d-block mt-2" muted>
                When disabled, all created or updated customers are synchronized without reading the Business Central selection field.
              </Form.Text>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="bc-customer-match-field">
            <Form.Label column sm={6}>
              Business Central match field
            </Form.Label>
            <Col sm={6}>
              <FloatingLabel controlId="bc-customer-match-field" label="Select match field">
                <Form.Select
                  value="number"
                  onChange={() => setDynamicsBcUserDefaultData?.({ ...dynamicsBcUserDefaultData, bc_customer_match_field: 'number' })}
                >
                  <option value="number">Customer number</option>
                </Form.Select>
              </FloatingLabel>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="rentman-customer-custom-field">
            <Form.Label column sm={6}>
              Rentman customer custom field
            </Form.Label>
            <Col sm={6}>
              <FloatingLabel controlId="rentman-customer-custom-field" label="Customer number custom field">
                <Form.Control
                  value={dynamicsBcUserDefaultData.rentman_customer_custom_field || ''}
                  placeholder="custom_12"
                  onChange={(event) =>
                    setDynamicsBcUserDefaultData?.({ ...dynamicsBcUserDefaultData, rentman_customer_custom_field: event.target.value.trim() })
                  }
                />
              </FloatingLabel>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="rentman-contact-person-custom-field">
            <Form.Label column sm={6}>
              Rentman contact person custom field
            </Form.Label>
            <Col sm={6}>
              <FloatingLabel controlId="rentman-contact-person-custom-field" label="Contact number custom field">
                <Form.Control
                  value={dynamicsBcUserDefaultData.rentman_contact_person_custom_field || ''}
                  placeholder="custom_13"
                  onChange={(event) =>
                    setDynamicsBcUserDefaultData?.({ ...dynamicsBcUserDefaultData, rentman_contact_person_custom_field: event.target.value.trim() })
                  }
                />
              </FloatingLabel>
              <Form.Text className="d-block mt-2" muted>
                Create a text custom field on Rentman contact persons. It stores the Business Central contact number automatically.
              </Form.Text>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="rentman-customer-gln-custom-field">
            <Form.Label column sm={6}>
              Rentman GLN/EAN custom field
            </Form.Label>
            <Col sm={6}>
              <FloatingLabel controlId="rentman-customer-gln-custom-field" label="GLN/EAN custom field">
                <Form.Control
                  value={dynamicsBcUserDefaultData.rentman_customer_gln_custom_field || ''}
                  placeholder="custom_3"
                  onChange={(event) =>
                    setDynamicsBcUserDefaultData?.({ ...dynamicsBcUserDefaultData, rentman_customer_gln_custom_field: event.target.value.trim() })
                  }
                />
              </FloatingLabel>
              <Form.Text className="d-block mt-2" muted>
                Optional. Enter the API name of a text field on Rentman customers to copy GLN/EAN from Business Central. Leave empty to disable. An empty
                GLN/EAN in Business Central clears this field in Rentman.
              </Form.Text>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="customer-gln-source">
            <Form.Label column sm={6}>
              GLN/EAN source
            </Form.Label>
            <Col sm={6}>
              <FloatingLabel controlId="customer-gln-source" label="Select GLN/EAN source">
                <Form.Select
                  value={dynamicsBcUserDefaultData.customer_gln_source || 'rentmanExtension'}
                  onChange={(event) =>
                    setDynamicsBcUserDefaultData?.({ ...dynamicsBcUserDefaultData, customer_gln_source: event.target.value as 'rentmanExtension' | 'abakion' })
                  }
                >
                  <option value="rentmanExtension">Rentman Integration Extension</option>
                  <option value="abakion">Abakion Data Access API</option>
                </Form.Select>
              </FloatingLabel>
              <Form.Text className="d-block mt-2" muted>
                {dynamicsBcUserDefaultData.customer_gln_source === 'abakion'
                  ? 'Requires the Abakion Data Access API and read access. Uses the saved Business Central environment and company.'
                  : 'Requires Rentman Integration Extension 1.0.0.8 or later.'}{' '}
                Only used when a GLN/EAN custom field is configured. Save settings, then use Re-sync on existing customers to apply changes.
              </Form.Text>
            </Col>
          </Form.Group>
          {folderError && <Alert variant="danger">{folderError}</Alert>}
          <Form.Group as={Row} className="mb-3" controlId="rentman-customer-folder">
            <Form.Label column sm={6}>
              Folder for new Rentman customers
            </Form.Label>
            <Col sm={6}>
              <FloatingLabel controlId="rentman-customer-folder" label="Select customer folder">
                <Form.Select
                  value={dynamicsBcUserDefaultData.rentman_customer_folder || ''}
                  onChange={(event) => setDynamicsBcUserDefaultData?.({ ...dynamicsBcUserDefaultData, rentman_customer_folder: event.target.value })}
                >
                  <option value="">Select customer folder</option>
                  {dynamicsBcUserDefaultData.rentman_customer_folder &&
                    !folders.some((folder) => `/folders/${folder.id}` === dynamicsBcUserDefaultData.rentman_customer_folder) && (
                      <option value={dynamicsBcUserDefaultData.rentman_customer_folder}>{dynamicsBcUserDefaultData.rentman_customer_folder}</option>
                    )}
                  {folders.map((folder) => (
                    <option key={folder.id} value={`/folders/${folder.id}`}>
                      {folder.path || folder.name}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm={6}>
              Existing customers and contact persons
            </Form.Label>
            <Col sm={6}>
              <Button variant="primary" type="button" disabled={importing} onClick={() => setConfirmImport(true)}>
                {importing && <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />}
                {importing ? 'Importing links…' : 'Import existing links'}
              </Button>
              <Form.Text className="d-block mt-2" muted>
                Link existing customers and contact persons using their BC numbers. Configure the contact person custom field to include contacts. Save settings
                before importing. Customer and contact data stays unchanged.
              </Form.Text>
              <Modal show={confirmImport} onHide={() => setConfirmImport(false)} centered aria-labelledby="customer-links-confirmation-title">
                <Modal.Header closeButton>
                  <Modal.Title id="customer-links-confirmation-title">Import existing links?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <p>
                    Link existing BC and Rentman customers by their BC customer number, and contact persons within matched customers by their BC contact number.
                    Customer and contact data stays unchanged.
                  </p>
                  <p className="text-muted small">
                    Uses saved settings and checks all customers, including those not selected for synchronization. Missing matches and conflicts are reported.
                  </p>
                  <Alert variant="warning" className="mb-0">
                    When the BC customer webhook is active, deleting a linked customer in Business Central also deletes the linked Rentman customer, subject to
                    Rentman&apos;s deletion rules. The contact webhook also synchronizes deletion of linked contact persons.
                  </Alert>
                </Modal.Body>
                <Modal.Footer>
                  <Button type="button" variant="secondary" onClick={() => setConfirmImport(false)}>
                    Cancel
                  </Button>
                  <Button type="button" variant="primary" disabled={importing} onClick={importLinks}>
                    Confirm import
                  </Button>
                </Modal.Footer>
              </Modal>
              {importError && (
                <Alert variant="danger" className="mt-3 mb-0">
                  {importError}
                </Alert>
              )}
              {importResult && (
                <Alert
                  variant={importResult.issues.length || importResult.contactLinks?.issues.length ? 'warning' : 'success'}
                  className="mt-3 mb-0"
                  aria-live="polite"
                >
                  <p className="mb-0">
                    {importResult.linked} customer links saved. {importResult.issues.length} BC customers skipped. {importResult.unmatchedRentman} Rentman
                    customers without a BC number match.
                  </p>
                  {importResult.contactLinks && (
                    <p className="mt-2 mb-0">
                      {importResult.contactLinks.linked} contact links saved. {importResult.contactLinks.issues.length} BC contacts skipped.
                    </p>
                  )}
                  {importResult.issues.length > 0 && (
                    <details className="mt-2">
                      <summary>Show skipped customers</summary>
                      <div className="overflow-auto mt-2" style={{ maxHeight: 240 }}>
                        <Table size="sm" responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Customer number</th>
                              <th>Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {importResult.issues.map((issue, index) => (
                              <tr key={index}>
                                <td className="text-nowrap">{issue.customerNumber || '(empty number)'}</td>
                                <td>{issue.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </details>
                  )}
                  {!!importResult.contactLinks?.issues.length && (
                    <details className="mt-2">
                      <summary>Show skipped contact persons</summary>
                      <div className="overflow-auto mt-2" style={{ maxHeight: 240 }}>
                        <Table size="sm" responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>Customer number</th>
                              <th>Contact number</th>
                              <th>Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {importResult.contactLinks.issues.map((issue, index) => (
                              <tr key={index}>
                                <td className="text-nowrap">{issue.customerNumber || '(empty number)'}</td>
                                <td className="text-nowrap">{issue.contactNumber || '(empty number)'}</td>
                                <td>{issue.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </details>
                  )}
                </Alert>
              )}
            </Col>
          </Form.Group>
        </>
      ) : (
        <>
          <CustomerContactPriorities dynamicsBcUserDefaultData={dynamicsBcUserDefaultData} setDynamicsBcUserDefaultData={setDynamicsBcUserDefaultData} />
          <DynamicsBcDefaultShipmentMethod dynamicsBcUserDefaultData={dynamicsBcUserDefaultData} setDynamicsBcUserDefaultData={setDynamicsBcUserDefaultData} />
        </>
      )}
    </>
  );
};
